import React, { useEffect, useState } from 'react';
import { useParams, useLocation, NavLink } from 'react-router-dom';
import Swal from 'sweetalert2';

// @mui
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import TableHeader from 'components/Table/TableHeader';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';

import * as Func from 'services/Function';
import CountingSearchProduct from './CountingSearchProduct';
import {
  mockGetSession,
  mockCreateSession,
  mockUpdateSession,
  mockChangeSessionStatus,
  mockSaveSessionItem,
  mockGetStockTake,
} from './mockApi';

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_MAP = {
  draft: { label: 'ร่าง', color: 'default', icon: 'edit_note' },
  active: { label: 'กำลังนับ', color: 'warning', icon: 'inventory' },
  completed: { label: 'เสร็จสิ้น', color: 'success', icon: 'task_alt' },
  cancelled: { label: 'ยกเลิก', color: 'error', icon: 'cancel' },
};

const initAttr = {
  id: '',
  code: '',
  name: '',
  remark: '',
  status: 'draft',
  stock_take_id: null,
  stock_take_code: '',
  items: [],
  created_at: '',
  created_by_name: '',
};

const initForm = { name: '', remark: '' };

// ── Component ──────────────────────────────────────────────────────────────────

function CountingSession() {
  const { t } = useTranslation();
  const { id } = useParams(); // 'new' or numeric id
  const location = useLocation();

  const isNew = id === 'new';
  const stockTakeId = new URLSearchParams(location.search).get('stock_take_id');

  const [attr, setAttr] = useState(initAttr);
  const [form, setForm] = useState(initForm);
  const [masterProducts, setMasterProducts] = useState([]);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [modalProduct, setModalProduct] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);

  // local qty edits: { [barcode_id]: qty }
  const [qtyMap, setQtyMap] = useState({});
  const [savingRow, setSavingRow] = useState(null);

  // ── Notify ────────────────────────────────────────────────────────────────────
  const showNotify = (msg, severity = 'success') => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotify(null)}
          severity={severity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  // ── Load ──────────────────────────────────────────────────────────────────────
  const loadSession = () => {
    setLoading(true);
    mockGetSession(id).then((result) => {
      if (result.status === 'ok') {
        const s = result.data;
        setAttr(s ?? initAttr);
        // init qty map from existing items
        const map = {};
        (s?.items ?? []).forEach((item) => {
          map[item.barcode_id] = item.counted_qty ?? '';
        });
        setQtyMap(map);
        // load master products for the search filter
        if (s?.stock_take_id) {
          mockGetStockTake(s.stock_take_id).then((r) => {
            if (r.status === 'ok') setMasterProducts(r.data?.products ?? []);
          });
        }
      }
      setLoading(false);
    });
  };

  const loadMasterProducts = () => {
    if (stockTakeId) {
      mockGetStockTake(stockTakeId).then((r) => {
        if (r.status === 'ok') setMasterProducts(r.data?.products ?? []);
      });
    }
  };

  useEffect(() => {
    if (isNew) {
      setAttr({ ...initAttr, stock_take_id: stockTakeId });
      setForm(initForm);
      setQtyMap({});
      loadMasterProducts();
    } else {
      loadSession();
    }
  }, [id]);

  // ── Create session ────────────────────────────────────────────────────────────
  const createSession = () => {
    if (!form.name.trim()) {
      showNotify('กรุณากรอกชื่อครั้งนับ', 'error');
      return;
    }
    setLoading(true);
    setConfirmCreate(false);
    mockCreateSession({ stock_take_id: stockTakeId, name: form.name, remark: form.remark }).then(
      (result) => {
        setLoading(false);
        if (result.status === 'ok') {
          window.location.replace(`/counting_session/${result.id}`);
        } else {
          showNotify('เกิดข้อผิดพลาด กรุณาลองใหม่', 'error');
        }
      }
    );
  };

  // ── Save info ─────────────────────────────────────────────────────────────────
  const saveInfo = () => {
    setLoading(true);
    setConfirmSave(false);
    mockUpdateSession(id, { name: attr.name, remark: attr.remark }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        showNotify('บันทึกสำเร็จ');
      } else {
        showNotify('เกิดข้อผิดพลาด', 'error');
      }
    });
  };

  // ── Change status ─────────────────────────────────────────────────────────────
  const changeStatus = (status) => {
    Swal.fire({
      title: `เปลี่ยนสถานะเป็น "${STATUS_MAP[status]?.label}"?`,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: 'ยืนยัน',
      denyButtonText: 'ยกเลิก',
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        mockChangeSessionStatus(id, status).then((result) => {
          if (result.status === 'ok') {
            loadSession();
            showNotify('เปลี่ยนสถานะสำเร็จ');
          } else {
            setLoading(false);
            showNotify('เกิดข้อผิดพลาด', 'error');
          }
        });
      }
    });
  };

  // ── Add product to session ────────────────────────────────────────────────────
  const addProduct = (product) => {
    if (attr.items.find((i) => i.barcode_id === product.barcode_id)) {
      showNotify('สินค้านี้อยู่ในครั้งนับแล้ว', 'warning');
      return;
    }
    setAttr((prev) => ({
      ...prev,
      items: [...prev.items, { ...product, counted_qty: null, system_qty: 0 }],
    }));
    setQtyMap((prev) => ({ ...prev, [product.barcode_id]: '' }));
    showNotify('เพิ่มสินค้าเรียบร้อย');
  };

  // ── Save row qty ──────────────────────────────────────────────────────────────
  const saveRowQty = (item) => {
    const qty = parseFloat(qtyMap[item.barcode_id]);
    if (Number.isNaN(qty) || qty < 0) {
      showNotify('กรุณากรอกจำนวนที่ถูกต้อง', 'error');
      return;
    }
    setSavingRow(item.barcode_id);
    mockSaveSessionItem(id, item.barcode_id, qty).then((result) => {
      setSavingRow(null);
      if (result.status === 'ok') {
        setAttr((prev) => ({
          ...prev,
          items: prev.items.map((i) =>
            i.barcode_id === item.barcode_id ? { ...i, counted_qty: qty } : i
          ),
        }));
        showNotify(`บันทึก ${item.barcode} สำเร็จ`);
      } else {
        showNotify('เกิดข้อผิดพลาด', 'error');
      }
    });
  };

  // ── Progress ──────────────────────────────────────────────────────────────────
  const countedItems = attr.items.filter(
    (i) => i.counted_qty !== null && i.counted_qty !== undefined
  ).length;
  const totalItems = attr.items.length;
  const progressPct = totalItems ? Math.round((countedItems / totalItems) * 100) : 0;

  const editable = attr.status === 'draft' || attr.status === 'active';

  // ── Status chip ───────────────────────────────────────────────────────────────
  const renderStatus = (status) => {
    const s = STATUS_MAP[status] ?? { label: status, color: 'default', icon: 'help' };
    return (
      <Chip
        icon={<Icon sx={{ fontSize: '1rem !important' }}>{s.icon}</Icon>}
        label={s.label}
        color={s.color === 'default' ? undefined : s.color}
        size='small'
        variant='outlined'
      />
    );
  };

  const renderLoading = () => (
    <Backdrop sx={{ color: '#fff', zIndex: (th) => th.zIndex.drawer + 1 }} open={loading}>
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  const itemHeaders = [
    { id: 'pd.barcode', label: 'Barcode', align: 'left', width: 140, sort: false },
    { id: 'pd.name', label: 'สินค้า', align: 'left', sort: false },
    { id: 'pd.unit_name', label: 'หน่วย', align: 'center', width: 80, sort: false },
    { id: 'pd.system_qty', label: 'จำนวนระบบ', align: 'center', width: 110, sort: false },
    { id: 'pd.counted_qty', label: 'จำนวนนับได้', align: 'center', width: 150, sort: false },
    { id: 'pd.diff', label: 'ผลต่าง', align: 'center', width: 100, sort: false },
    { id: 'action', label: 'บันทึก', align: 'center', width: 80, sort: false },
  ];

  // ── NEW SESSION FORM ──────────────────────────────────────────────────────────
  if (isNew) {
    return (
      <DashboardLayout>
        <Navbar route={['คลังสินค้า', 'ใบนับสต็อก', 'สร้างครั้งนับใหม่']} />
        <MDBox mb={7}>
          <Grid container spacing={2} justifyContent='center'>
            <Grid item xs={12} sm={8} md={6}>
              <Card>
                <MDBox p={4}>
                  <MDTypography variant='h5' fontWeight='bold' mb={3}>
                    สร้างครั้งนับใหม่
                  </MDTypography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <MDInput
                        label='ชื่อครั้งนับ *'
                        fullWidth
                        variant='standard'
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        inputProps={{ autoComplete: 'off' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDInput
                        label='หมายเหตุ'
                        fullWidth
                        variant='standard'
                        value={form.remark}
                        onChange={(e) => setForm({ ...form, remark: e.target.value })}
                        inputProps={{ autoComplete: 'off' }}
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12} display='flex' justifyContent='space-between' mt={1}>
                      {stockTakeId && (
                        <NavLink to={`/counts_round/${stockTakeId}`}>
                          <MDButton variant='text' color='secondary'>
                            <Icon>arrow_back</Icon>&nbsp;ยกเลิก
                          </MDButton>
                        </NavLink>
                      )}
                      <MDBox display='flex' gap={1}>
                        {confirmCreate ? (
                          <>
                            <MDButton
                              variant='text'
                              color='error'
                              onClick={() => setConfirmCreate(false)}
                            >
                              <Icon>cancel</Icon>&nbsp;{t('element.cancel')}
                            </MDButton>
                            <MDButton variant='outlined' color='success' onClick={createSession}>
                              <Icon>save</Icon>&nbsp;{t('element.saveconfirm')}
                            </MDButton>
                          </>
                        ) : (
                          <MDButton
                            variant='contained'
                            color='dark'
                            onClick={() => setConfirmCreate(true)}
                          >
                            <Icon>save</Icon>&nbsp;สร้างครั้งนับ
                          </MDButton>
                        )}
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
        {renderLoading()}
        {notify}
      </DashboardLayout>
    );
  }

  // ── SESSION DETAIL ────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <Navbar route={['คลังสินค้า', 'ใบนับสต็อก', attr.stock_take_code, attr.code || `#${id}`]} />
      <MDBox mb={7}>
        <Grid container spacing={2}>
          {/* Back */}
          <Grid item xs={12}>
            <NavLink
              to={attr.stock_take_id ? `/counts_round/${attr.stock_take_id}` : '/counts_round'}
            >
              <MDButton variant='text' color='secondary' size='small'>
                <Icon>arrow_back</Icon>&nbsp;กลับใบนับหลัก
              </MDButton>
            </NavLink>
          </Grid>

          {/* Header info card */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} md={7}>
                    <MDTypography variant='h5' fontWeight='bold'>
                      {attr.code}
                    </MDTypography>
                    <MDTypography variant='caption' color='secondary'>
                      ใบนับหลัก: {attr.stock_take_code}&nbsp;·&nbsp; สร้างเมื่อ{' '}
                      {Func.dateTimeDisplay(attr.created_at)}&nbsp;·&nbsp; โดย{' '}
                      {attr.created_by_name}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <MDBox
                      display='flex'
                      justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                      gap={1}
                      flexWrap='wrap'
                    >
                      {renderStatus(attr.status)}
                      {attr.status === 'draft' && (
                        <MDButton
                          variant='outlined'
                          color='warning'
                          size='small'
                          onClick={() => changeStatus('active')}
                        >
                          <Icon>play_arrow</Icon>&nbsp;เริ่มนับ
                        </MDButton>
                      )}
                      {attr.status === 'active' && (
                        <MDButton
                          variant='outlined'
                          color='success'
                          size='small'
                          onClick={() => changeStatus('completed')}
                        >
                          <Icon>task_alt</Icon>&nbsp;เสร็จสิ้น
                        </MDButton>
                      )}
                      {editable && (
                        <MDButton
                          variant='outlined'
                          color='error'
                          size='small'
                          onClick={() => changeStatus('cancelled')}
                        >
                          <Icon>cancel</Icon>&nbsp;ยกเลิก
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Progress */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <MDBox p={3}>
                <MDTypography variant='h6' fontWeight='medium' mb={2}>
                  ความคืบหน้า
                </MDTypography>
                <MDBox display='flex' justifyContent='space-between' mb={1}>
                  <MDTypography variant='caption'>
                    นับแล้ว {countedItems} / {totalItems} รายการ
                  </MDTypography>
                  <MDTypography variant='caption' fontWeight='bold'>
                    {progressPct}%
                  </MDTypography>
                </MDBox>
                <LinearProgress
                  variant='determinate'
                  value={progressPct}
                  color={progressPct === 100 ? 'success' : 'info'}
                  sx={{ height: 10, borderRadius: 5 }}
                />

                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12}>
                    <MDInput
                      label='ชื่อครั้งนับ'
                      fullWidth
                      variant='standard'
                      value={attr.name}
                      onChange={(e) => setAttr({ ...attr, name: e.target.value })}
                      disabled={!editable}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MDInput
                      label='หมายเหตุ'
                      fullWidth
                      variant='standard'
                      value={attr.remark}
                      onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
                      disabled={!editable}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  {editable && (
                    <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
                      {confirmSave ? (
                        <>
                          <MDButton
                            variant='text'
                            color='error'
                            onClick={() => setConfirmSave(false)}
                          >
                            <Icon>cancel</Icon>&nbsp;{t('element.cancel')}
                          </MDButton>
                          <MDButton variant='outlined' color='success' onClick={saveInfo}>
                            <Icon>save</Icon>&nbsp;{t('element.saveconfirm')}
                          </MDButton>
                        </>
                      ) : (
                        <MDButton variant='text' color='info' onClick={() => setConfirmSave(true)}>
                          <Icon>save</Icon>&nbsp;{t('element.save')}
                        </MDButton>
                      )}
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Items table */}
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox p={3}>
                <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={2}>
                  <MDTypography variant='h6' fontWeight='medium'>
                    รายการสินค้าในครั้งนับ ({attr.items.length} รายการ)
                  </MDTypography>
                  {editable && (
                    <MDButton
                      variant='outlined'
                      color='info'
                      size='small'
                      onClick={() => setModalProduct(true)}
                    >
                      <Icon>add</Icon>&nbsp;เพิ่มสินค้า
                    </MDButton>
                  )}
                </MDBox>

                <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
                  <Table size='small' stickyHeader>
                    <TableHeader
                      order='asc'
                      orderBy=''
                      onRequestSort={() => {}}
                      data={itemHeaders}
                    />
                    <TableBody>
                      {attr.items.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={itemHeaders.length} align='center' sx={{ py: 4 }}>
                            <MDTypography variant='caption' color='secondary'>
                              ยังไม่มีสินค้าในครั้งนับ กรุณาเพิ่มสินค้า
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      )}
                      {attr.items.map((item) => {
                        const counted = item.counted_qty ?? null;
                        const systemQty = item.system_qty ?? 0;
                        const diff = counted !== null ? counted - systemQty : null;
                        const isSaving = savingRow === item.barcode_id;
                        const hasCounted = counted !== null;
                        return (
                          <TableRow key={item.barcode_id} hover>
                            <TableCell align='left'>
                              <MDTypography variant='caption' fontWeight='medium'>
                                {item.barcode}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='left'>
                              <MDTypography variant='caption'>{item.name}</MDTypography>
                            </TableCell>
                            <TableCell align='center'>
                              <MDTypography variant='caption' color='secondary'>
                                {item.unit_name}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='center'>
                              <MDTypography variant='caption'>
                                {Func.numberDisplay(systemQty)}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='center'>
                              {editable ? (
                                <MDInput
                                  value={qtyMap[item.barcode_id] ?? ''}
                                  onChange={(e) =>
                                    setQtyMap((prev) => ({
                                      ...prev,
                                      [item.barcode_id]: e.target.value,
                                    }))
                                  }
                                  type='number'
                                  inputProps={{
                                    min: 0,
                                    step: 0.01,
                                    style: { textAlign: 'right', width: 80 },
                                  }}
                                  size='small'
                                />
                              ) : (
                                <MDTypography variant='caption'>
                                  {hasCounted ? Func.numberDisplay(counted) : '-'}
                                </MDTypography>
                              )}
                            </TableCell>
                            <TableCell align='center'>
                              {diff !== null ? (
                                <MDTypography
                                  variant='caption'
                                  fontWeight='bold'
                                  color={diff === 0 ? 'success' : diff > 0 ? 'info' : 'error'}
                                >
                                  {diff > 0 ? '+' : ''}
                                  {Func.numberDisplay(diff)}
                                </MDTypography>
                              ) : (
                                <MDTypography variant='caption' color='secondary'>
                                  -
                                </MDTypography>
                              )}
                            </TableCell>
                            <TableCell align='center'>
                              {editable && (
                                <Tooltip title='บันทึกจำนวน'>
                                  <span>
                                    <IconButton
                                      size='small'
                                      color='success'
                                      disabled={isSaving}
                                      onClick={() => saveRowQty(item)}
                                    >
                                      {isSaving ? (
                                        <CircularProgress size={16} color='inherit' />
                                      ) : (
                                        <Icon>save</Icon>
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Product search modal — filtered to master's products */}
      <CountingSearchProduct
        open={modalProduct}
        onClose={() => setModalProduct(false)}
        onAdd={addProduct}
        selectedIds={attr.items.map((i) => i.barcode_id)}
        filterIds={masterProducts.map((p) => p.barcode_id)}
      />

      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default CountingSession;
